// ===== STYLES =====
import "./TableNfe.css";

// ===== REACT =====
import * as React from "react";

// ===== ROUTER =====
import { useNavigate } from "react-router-dom";

// ===== I18N =====
import { useTranslation } from "react-i18next";

// ===== UI (MUI) =====
import {
  Box,
  Paper,
  Divider,
  Button,
  Checkbox,
  DialogActions,
  IconButton,
  FormControlLabel,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Snackbar,
  Alert,
  TextField,
  CircularProgress,
} from "@mui/material";

// ===== MUI COMPONENTS =====
import { DataGrid } from "@mui/x-data-grid";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

// ===== DND =====
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ===== UTILS =====
import { parseNfeXml } from "../../../utilities/Utilities";
import { translateDbLabel } from "../../../utilities/translateDbLabel";
import { translateBackendMessage } from "../../../utilities/translateBackendMessage";
import { mapParsedNfeToDanfeData } from "../../../utilities/mapParsedNfeToDanfeData";

// ===== SERVICES =====
import {
  fetchFileXml,
  downloadXmlBatch,
  downloadDanfePdfBatch,
} from "../../../../src/services/fileXmlService";
import { continueProcess } from "../../../services/processContinueService";
import { deleteNfes } from "../../../services/nfeDeleteService";
import { deleteCtes } from "../../../services/cteDeleteService";
import { fetchDocumentosNfe } from "../../../services/nfeItemService";

// ===== COMPONENTS =====
import CustomToolbar from "../../customToolbar/CustomToolbar";
import GeneratedDocsDialog from "../../document/GeneratedDocsDialog";
import ColumnsNfe from "./ColumnsNfe";
import ColumnsCte from "./ColumnsCte";

// ===== LIBS =====
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";

export default function TableNfe({ docType = "nfe", rows = [], onRefresh, }) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");

  const safeRows = Array.isArray(rows) ? rows : [];

  const gridRows = safeRows.map((r, idx) => ({
    id: r.accesskey ?? r.acesskey ?? r.access_key_cte ?? idx,
    ...r,
  }));

  const gridColumnsBase = React.useMemo(() => {
    return docType === "nfe" ? ColumnsNfe(t, isEnglish) : ColumnsCte(t);
  }, [docType, t, isEnglish]);

  const STORAGE_KEY = `grid-config-${docType}`;

  const [columnOrder, setColumnOrder] = React.useState(
    gridColumnsBase.map((col) => col.field),
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});

  const [openColumnConfig, setOpenColumnConfig] = React.useState(false);

  const gridColumns = React.useMemo(() => {
    const map = new Map(gridColumnsBase.map((col) => [col.field, col]));
    return columnOrder.map((field) => map.get(field)).filter(Boolean);
  }, [gridColumnsBase, columnOrder]);

  React.useEffect(() => {
    const baseFields = gridColumnsBase.map((col) => col.field);
    const saved = localStorage.getItem(STORAGE_KEY);

    let nextOrder = baseFields;
    let nextVisibility = {};

    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        const validOrder = (parsed.columnOrder || []).filter((field) =>
          baseFields.includes(field),
        );

        const missingFields = baseFields.filter(
          (field) => !validOrder.includes(field),
        );

        nextOrder = [...validOrder, ...missingFields];
        nextVisibility = parsed.columnVisibilityModel || {};
      } catch (error) {
        console.error("Erro ao carregar configuração do grid", error);
      }
    }

    setColumnOrder((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextOrder) ? prev : nextOrder,
    );

    setColumnVisibilityModel((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextVisibility)
        ? prev
        : nextVisibility,
    );
  }, [STORAGE_KEY, gridColumnsBase]);

  const [rowSelectionModel, setRowSelectionModel] = React.useState({
    type: "include",
    ids: new Set(),
  });

  // Calcula selectedIds respeitando tanto "include" quanto "exclude" (select-all)
  const selectedIds = React.useMemo(() => {
    if (rowSelectionModel.type === "exclude") {
      const excluded = new Set(Array.from(rowSelectionModel.ids).map(String));
      return gridRows
        .map((r) => r.id)
        .filter((id) => !excluded.has(String(id)));
    }
    return Array.from(rowSelectionModel.ids);
  }, [rowSelectionModel, gridRows]);

  const selectedRows = React.useMemo(
    () => gridRows.filter((r) => selectedIds.includes(r.id)),
    [gridRows, selectedIds],
  );

  const ASSIGN_ALLOWED_FUNCTIONS = ["ZF_ASSIGN_PURCHASE_ORDER", "ZF_SIMULATE_INVOICE"];

  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;

  const selectedRow = selectedId
    ? gridRows.find((r) => String(r.id) === String(selectedId))
    : null;

  const canAssign =
    selectedIds.length === 1 &&
    ASSIGN_ALLOWED_FUNCTIONS.includes(selectedRow?.next_step_function);

  const ENTER_QTY_FUNCTIONS = ["ZF_CREATE_GOODS_RECEIPT", "ZF_ENTER_DANFE", "ZF_SEARCH_BATCH", "ZF_ENTER_GOODS_TRANSFER", "ZF_ENTER_TRANSFER_QTY"];

  const canEnterQty =
    selectedIds.length === 1 &&
    ENTER_QTY_FUNCTIONS.includes(selectedRow?.next_step_function);

  const [openDocs, setOpenDocs] = React.useState(false);
  const [docsList, setDocsList] = React.useState([]);
  const [docsLoading, setDocsLoading] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [notif, setNotif] = React.useState({ open: false, message: "", severity: "info" });
  const [consultaResult, setConsultaResult] = React.useState(null);
  const [rejeitarDialog, setRejeitarDialog] = React.useState({ open: false, tpEvento: null });
  const [xJust, setXJust] = React.useState("");
  const [rejeitarLoading, setRejeitarLoading] = React.useState(false);

  // Última sincronização (usada no badge "Hoje às HH:MM" da toolbar)
  const [lastSync, setLastSync] = React.useState(new Date());

  React.useEffect(() => {
    setLastSync(new Date());
  }, [rows]);

  const navigate = useNavigate();

  function handleAssign() {
    if (!selectedRow) return;

    if (!ASSIGN_ALLOWED_FUNCTIONS.includes(selectedRow?.next_step_function)) {
      const stepName = translateDbLabel(
        selectedRow?.next_step_description || selectedRow?.step_description || "",
        isEnglish,
      );
      setNotif({
        open: true,
        message: stepName
          ? t("nfe.error.assignmentNotAllowedStep", { step: stepName })
          : t("nfe.error.assignmentNotAllowed"),
        severity: "warning",
      });
      return;
    }

    const accessKey =
      selectedRow?.acesskey ??
      selectedRow?.accesskey ??
      selectedRow?.access_key_cte ??
      "";

    if (!accessKey) return;

    navigate(`/inbound/assign/${accessKey}`, {
      state: {
        docType,
        selected: selectedRow,
      },
    });
  }

  function handleUpload() {
    navigate("/upload");
  }

  const handleShowDanfe = async () => {
    try {
      if (!selectedRow) {
        throw new Error(t("errors.noRowSelected"));
      }

      const accessKey =
        selectedRow?.acesskey ??
        selectedRow?.accesskey ??
        selectedRow?.access_key_cte ??
        "";

      if (!accessKey) {
        throw new Error(t("errors.accessKeyNotFound"));
      }

      const result = await fetchFileXml(accessKey);

      if (!result?.xml) {
        throw new Error(t("errors.xmlNotReturned"));
      }

      const parsedXml = parseNfeXml(result.xml, t);
      const danfeData = mapParsedNfeToDanfeData(parsedXml);

      navigate(`/inbound/danfe/${accessKey}`, {
        state: {
          data: danfeData,
        },
      });
    } catch (error) {
      console.error("Erro ao abrir DANFE:", error);
    }
  };

  const handleConsultarStatus = async () => {
    if (!selectedRow) return;
    const accesskey = selectedRow?.accesskey ?? selectedRow?.acesskey ?? selectedRow?.access_key_cte ?? "";
    if (!accesskey) return;
    try {
      const { API_BASE_URL } = await import("../../../config/api");
      const res  = await fetch(`${API_BASE_URL}/nfe/consulta-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accesskey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || data?.error || "Erro ao consultar SEFAZ");
      setConsultaResult(data);
      onRefresh?.();
    } catch (err) {
      setNotif({ open: true, message: translateBackendMessage(err.message, isEnglish), severity: "error" });
    }
  };

  function handleRejeitar(tpEvento) {
    setXJust("");
    setRejeitarDialog({ open: true, tpEvento });
  }

  async function handleRejeitarConfirm() {
    if (!selectedRow) return;
    const accesskey = selectedRow?.accesskey ?? selectedRow?.acesskey ?? selectedRow?.access_key_cte ?? "";
    if (!accesskey) return;
    if (rejeitarDialog.tpEvento === "210240" && xJust.trim().length < 15) {
      setNotif({ open: true, message: t("nfe.validation.justificationMin"), severity: "warning" });
      return;
    }
    setRejeitarLoading(true);
    try {
      const { API_BASE_URL } = await import("../../../config/api");
      const body = { accesskey, tpEvento: rejeitarDialog.tpEvento };
      if (rejeitarDialog.tpEvento === "210240") body.xJust = xJust.trim();
      const res = await fetch(`${API_BASE_URL}/nfe/evento`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || data?.error || "Erro ao enviar evento");
      setRejeitarDialog({ open: false, tpEvento: null });
      const msgKey = rejeitarDialog.tpEvento === "210220" ? "nfe.evento.successDesconhecimento" : "nfe.evento.successNaoRealizada";
      setNotif({ open: true, message: t(msgKey), severity: "success" });
      onRefresh?.();
    } catch (err) {
      setNotif({ open: true, message: translateBackendMessage(err.message, isEnglish), severity: "error" });
    } finally {
      setRejeitarLoading(false);
    }
  }

  function handleEnterQty() {
    if (!selectedRow) return;

    if (!ENTER_QTY_FUNCTIONS.includes(selectedRow?.next_step_function)) {
      const stepName = translateDbLabel(
        selectedRow?.next_step_description || selectedRow?.step_description || "",
        isEnglish,
      );
      setNotif({
        open: true,
        message: stepName
          ? t("nfe.error.qtyNotAllowedStep", { step: stepName })
          : t("nfe.error.qtyNotAllowed"),
        severity: "warning",
      });
      return;
    }

    const accessKey =
      selectedRow?.acesskey ??
      selectedRow?.accesskey ??
      selectedRow?.access_key_cte ??
      "";

    if (!accessKey) return;

    navigate(`/inbound/enterQty/${accessKey}`, {
      state: {
        docType,
        selected: selectedRow,
      },
    });
  }

  function handleSimulate() {
    if (!selectedRow) return;
    navigate(`/inbound/simulation/${selectedRow.id}`, {
      state: {
        docType,
        selected: selectedRow,
      },
    });
  }

  async function handleContinue() {
    if (!selectedRow) return;

    if (selectedRow?.next_step_function === "ZF_ASSIGN_PURCHASE_ORDER") {
      const accessKey = selectedRow?.accesskey ?? selectedRow?.acesskey ?? '';
      navigate(`/inbound/assign/${accessKey}`, {
        state: { docType, selected: selectedRow },
      });
      return;
    }

    if (ENTER_QTY_FUNCTIONS.includes(selectedRow?.next_step_function)) {
      const accessKey = selectedRow?.accesskey ?? selectedRow?.acesskey ?? '';
      navigate(`/inbound/enterQty/${accessKey}`, {
        state: { docType, selected: selectedRow },
      });
      return;
    }

    const result = await continueProcess(selectedRow.accesskey, {
      selected: selectedRow,
      docType,
    });

    const waitingStep = result?.stepsExecuted?.find((s) => s.status === 'waiting');

    if (ENTER_QTY_FUNCTIONS.includes(waitingStep?.function_tx)) {
      const accessKey = selectedRow?.accesskey ?? selectedRow?.acesskey ?? '';
      navigate(`/inbound/enterQty/${accessKey}`, {
        state: { docType, selected: selectedRow },
      });
      return;
    }

    if (ASSIGN_ALLOWED_FUNCTIONS.includes(waitingStep?.function_tx)) {
      const accessKey = selectedRow?.accesskey ?? selectedRow?.acesskey ?? '';
      navigate(`/inbound/assign/${accessKey}`, {
        state: { docType, selected: selectedRow },
      });
      return;
    }

    if (waitingStep) {
      setNotif({ open: true, message: waitingStep.message, severity: "warning" });
      if (onRefresh) await onRefresh();
      return;
    }

    if (onRefresh) await onRefresh();
  }

  async function handleOpenDoc() {
    if (!selectedRow) return;

    const accessKey =
      selectedRow?.acesskey ??
      selectedRow?.accesskey ??
      selectedRow?.access_key_cte ??
      "";

    if (!accessKey) return;

    setDocsList([]);
    setDocsLoading(true);
    setOpenDocs(true);

    try {
      const data = await fetchDocumentosNfe(accessKey);
      setDocsList(data || []);
    } catch (error) {
      console.error("Erro ao buscar documentos gerados:", error);
      setDocsList([]);
    } finally {
      setDocsLoading(false);
    }
  }

  async function handleDeleteConfirm() {
    const accessKeys = selectedRows
      .map((row) => row?.acesskey ?? row?.accesskey ?? row?.access_key_cte ?? row?.id)
      .filter(Boolean);

    if (!accessKeys.length) return;

    setDeleteLoading(true);
    try {
      if (docType === "cte") {
        await deleteCtes(accessKeys);
      } else {
        await deleteNfes(accessKeys);
      }
      setOpenDeleteConfirm(false);
      setRowSelectionModel({ type: "include", ids: new Set() });
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error("Erro ao apagar documento:", error);
    } finally {
      setDeleteLoading(false);
    }
  }

  function handleSaveColumnConfig() {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        columnOrder,
        columnVisibilityModel,
      }),
    );
    setOpenColumnConfig(false);
  }

  async function handleDownloadDanfeBatch() {
    try {
      if (!selectedRows.length) {
        throw new Error("Nenhuma nota selecionada");
      }

      const accessKeys = selectedRows
        .map(
          (row) =>
            row?.acesskey ?? row?.accesskey ?? row?.access_key_cte ?? row?.id,
        )
        .filter(Boolean);

      if (!accessKeys.length) {
        throw new Error("Nenhuma access key válida encontrada");
      }

      const zipBlob = await downloadDanfePdfBatch(accessKeys);

      saveAs(
        zipBlob,
        `danfes_${new Date().toISOString().replace(/[:T]/g, "-").split(".")[0]}.zip`,
      );
    } catch (error) {
      console.error("Erro no download em massa de DANFE", error);
    }
  }

  async function handleDownloadXmlBatch() {
    try {
      if (!selectedRows.length) {
        throw new Error("Nenhuma nota selecionada");
      }

      const accessKeys = selectedRows
        .map(
          (row) =>
            row?.acesskey ?? row?.accesskey ?? row?.access_key_cte ?? row?.id,
        )
        .filter(Boolean);

      if (!accessKeys.length) {
        throw new Error("Nenhuma access key válida encontrada");
      }

      const zipBlob = await downloadXmlBatch(accessKeys);

      saveAs(
        zipBlob,
        `xmls_${new Date().toISOString().replace(/[:T]/g, "-").split(".")[0]}.zip`,
      );
    } catch (error) {
      console.error("Erro no download em massa de XML", error);
    }
  }

  function handleExportExcel() {
    const visibleColumns = gridColumns.filter(
      (col) => columnVisibilityModel[col.field] !== false,
    );

    const header = visibleColumns.map((c) => c.headerName);

    const fileName =
      "NFe_" +
      new Date().toISOString().replace(/[:T]/g, "-").split(".")[0] +
      ".xlsx";

    const dataRows = gridRows.map((row) =>
      visibleColumns.map((col) => row[col.field] ?? ""),
    );

    const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);

    visibleColumns.forEach((_, colIndex) => {
      const cell = XLSX.utils.encode_cell({ r: 0, c: colIndex });

      if (!ws[cell]) return;

      ws[cell].s = {
        font: {
          bold: true,
        },
      };
    });

    const colWidths = visibleColumns.map((col, i) => {
      const maxLength = Math.max(
        col.headerName.length,
        ...dataRows.map((r) => String(r[i] ?? "").length),
      );

      return { wch: maxLength + 2 };
    });

    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "NFe");

    XLSX.writeFile(wb, fileName);
  }

  function handleResetColumnConfig() {
    localStorage.removeItem(STORAGE_KEY);
    setColumnOrder(gridColumnsBase.map((col) => col.field));
    setColumnVisibilityModel({});
  }

  function SortableColumnItem({ field, column, isVisible, onToggleVisible }) {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: field });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <ListItem
        ref={setNodeRef}
        style={style}
        secondaryAction={
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab" }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        }
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isVisible}
              onChange={(e) => onToggleVisible(e.target.checked)}
            />
          }
          label={column.headerName}
          componentsProps={{ typography: { sx: { fontFamily: "'72', Arial, sans-serif", fontSize: 13 } } }}
        />
      </ListItem>
    );
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  return (
    <Box
      className="nfeGrid"
      sx={{
        p: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        variant="outlined"
        className="gridPaper"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Divider />
        <Box
          className="gridWrapper"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
          }}
        >
          <DataGrid
            rows={gridRows}
            columns={gridColumns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            getRowClassName={(params) => {
              const status = (params.row.execution_status || "").toUpperCase();
              const hasNextStep = !!params.row?.next_step_function;
              if (status.includes("E")) return "row-status-error";
              if (status.includes("S") && !hasNextStep) return "row-status-success";
              if (status.includes("S") && hasNextStep) return "row-status-warning";
              if (status.includes("W")) return "row-status-warning";
              return "";
            }}
            checkboxSelection
            disableRowSelectionOnClick
            density="compact"
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(model) => {
              // Preserva o type enviado pelo DataGrid (pode ser "include" ou "exclude")
              // type "exclude" ocorre ao clicar em "selecionar tudo"
              setRowSelectionModel({
                type: model?.type ?? "include",
                ids: new Set(Array.from(model?.ids ?? [])),
              });
            }}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{
              toolbar: {
                title: t("nfeTable.title"),
                docType,
                canSelect: selectedIds.length === 1,
                selectedCount: selectedIds.length,
                lastSync,
                canAssign,
                canEnterQty,
                onAssign: handleAssign,
                onEnterQty: handleEnterQty,
                onSimulate: handleSimulate,
                onContinue: handleContinue,
                onOpenDoc: handleOpenDoc,
                onUpload: handleUpload,
                onShowDanfe: handleShowDanfe,
                onConfigureColumns: () => setOpenColumnConfig(true),
                onExportExcel: handleExportExcel,
                onDownloadDanfeBatch: handleDownloadDanfeBatch,
                onDownloadXmlBatch: handleDownloadXmlBatch,
                selectedRow,
                canDelete: selectedIds.length > 0,
                onDelete: () => setOpenDeleteConfirm(true),
                onConsultarStatus: handleConsultarStatus,
                onRejeitar: handleRejeitar,
              },
            }}
            showToolbar
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "background.subtle",
                borderBottom: "1px solid",
                borderBottomColor: "divider",
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid",
                borderBottomColor: "divider",
              },
              "& .MuiDataGrid-cell": { outline: "none" },
            }}
          />
        </Box>
      </Paper>

      {/* Dialog: Configuração de Colunas */}

      {/* Dialog: Configuração de Colunas */}
      <Dialog
        open={openColumnConfig}
        onClose={() => setOpenColumnConfig(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}      >
        <DialogTitle sx={{ fontFamily: "'72', Arial, sans-serif", fontSize: 14, fontWeight: 600 }}>{t("buttons.settings")}</DialogTitle>

        <DialogContent dividers>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columnOrder}
              strategy={verticalListSortingStrategy}
            >
              <List dense>
                {columnOrder.map((field) => {
                  const column = gridColumnsBase.find(
                    (col) => col.field === field,
                  );
                  if (!column) return null;

                  const isVisible = columnVisibilityModel[field] !== false;

                  return (
                    <SortableColumnItem
                      key={field}
                      field={field}
                      column={column}
                      isVisible={isVisible}
                      onToggleVisible={(checked) =>
                        setColumnVisibilityModel((prev) => ({
                          ...prev,
                          [field]: checked,
                        }))
                      }
                    />
                  );
                })}
              </List>
            </SortableContext>
          </DndContext>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleResetColumnConfig}
            sx={{ textTransform: "none", fontFamily: "'72', Arial, sans-serif", fontSize: 13 }}
          >
            {t("buttons.bRestore")}
          </Button>
          <Button
            onClick={() => setOpenColumnConfig(false)}
            sx={{ textTransform: "none", fontFamily: "'72', Arial, sans-serif", fontSize: 13 }}
          >
            {t("buttons.bCancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveColumnConfig}
            sx={{ textTransform: "none", fontFamily: "'72', Arial, sans-serif", fontSize: 13 }}
          >
            {t("buttons.bSave")}
          </Button>
        </DialogActions>
      </Dialog>

      <GeneratedDocsDialog
        open={openDocs}
        onClose={() => setOpenDocs(false)}
        docs={docsList} 
        loading={docsLoading}
      />

 {/* Dialog: Confirmação de exclusão */}
<Dialog
  open={openDeleteConfirm}
  onClose={() => !deleteLoading && setOpenDeleteConfirm(false)}
  maxWidth="xs"
  fullWidth
  BackdropProps={{
    sx: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
  }}
  PaperProps={{
    sx: { borderRadius: "20px", overflow: "hidden" },
  }}
>
  <DialogTitle sx={{ p: 3, pb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box
      sx={{
        bgcolor: "rgba(211,47,47,0.10)",
        p: 1,
        borderRadius: "10px",
        display: "flex",
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
          stroke="#d32f2f"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 11v4M14 11v4"
          stroke="#d32f2f"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
      {t("buttons.deleteConfirmTitle")}
    </Typography>
  </DialogTitle>

  <Divider sx={{ borderColor: "divider" }} />

  <DialogContent sx={{ px: 3, py: 3 }}>
    <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.7 }}>
      {t("buttons.deleteConfirmMsg")}{" "}
      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
        {selectedIds.length}{" "}
        {docType === "cte"
          ? (selectedIds.length === 1 ? "CT-e" : "CT-es")
          : (selectedIds.length === 1 ? "nota fiscal" : "notas fiscais")}
      </Box>
      ? Esta ação não pode ser desfeita.
    </Typography>
  </DialogContent>

  <Box sx={{ px: 3, pb: 3, pt: 0, display: "flex", justifyContent: "flex-end", gap: 1 }}>
    <Button
      onClick={() => setOpenDeleteConfirm(false)}
      disabled={deleteLoading}
      variant="outlined"
      color="inherit"
      sx={{
        textTransform: "none",
        fontWeight: 600,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: "divider",
        color: "text.secondary",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {t("buttons.bCancel")}
    </Button>
    <Button
      onClick={handleDeleteConfirm}
      disabled={deleteLoading}
      disableElevation
      variant="contained"
      sx={{
        textTransform: "none",
        fontWeight: 600,
        borderRadius: "10px",
        backgroundColor: "#d32f2f",
        "&:hover": { backgroundColor: "#b71c1c" },
      }}
    >
      {deleteLoading ? <CircularProgress size={16} color="inherit" /> : t("buttons.bDelete")}
    </Button>
  </Box>
</Dialog>

      {/* Dialog resultado Consultar Status SEFAZ */}
      <Dialog
        open={!!consultaResult}
        onClose={() => setConsultaResult(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontFamily: "'72', Arial, sans-serif", fontWeight: 600, fontSize: 14, pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          {t("sefaz.consulta.title", { uf: consultaResult?.uf || "" })}
          <Typography component="span" sx={{ ml: 1, fontFamily: "'72', Arial, sans-serif", fontSize: 11, color: "text.secondary", fontWeight: 400 }}>
            {consultaResult?.ambiente}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {consultaResult && (() => {
            const ROW = ({ label, value, mono }) => (
              <Box sx={{ display: "flex", gap: 1, mb: 0.8 }}>
                <Typography sx={{ fontFamily: "'72', Arial, sans-serif", fontSize: 12, color: "text.secondary", minWidth: 130 }}>{label}</Typography>
                <Typography sx={{ fontFamily: mono ? "monospace" : "'72', Arial, sans-serif", fontSize: 12, fontWeight: 500, wordBreak: "break-all" }}>{value || "—"}</Typography>
              </Box>
            );
            const isOk = ["100", "150"].includes(consultaResult.cStat);
            const isCancelled = ["101", "151"].includes(consultaResult.cStat);
            return (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1.2, borderRadius: 1, backgroundColor: isOk ? "rgba(46,125,50,0.08)" : isCancelled ? "rgba(198,40,40,0.08)" : "background.subtle", border: "1px solid", borderColor: isOk ? "rgba(46,125,50,0.25)" : isCancelled ? "rgba(198,40,40,0.25)" : "divider" }}>
                  <Typography sx={{ fontFamily: "'72', Arial, sans-serif", fontSize: 13, fontWeight: 700, color: isOk ? "#2e7d32" : isCancelled ? "#c62828" : "text.primary" }}>
                    [{consultaResult.cStat}] {consultaResult.xMotivo}
                  </Typography>
                </Box>
                <ROW label={t("sefaz.distribute.colChave")} value={consultaResult.chNFe} mono />
                <ROW label={t("sefaz.consulta.protocol")}   value={consultaResult.protocolo} />
                <ROW label={t("sefaz.consulta.dhProtocolo")} value={consultaResult.dhProtocolo?.replace("T", " ").substring(0, 19)} />
                <ROW label={t("sefaz.consulta.dhRecbto")}   value={consultaResult.dhRecbto?.replace("T", " ").substring(0, 19)} />
                {consultaResult.xMotProt && <ROW label={t("sefaz.consulta.detalhe")} value={consultaResult.xMotProt} />}
              </Box>
            );
          })()}
        </DialogContent>

        <Divider sx={{ opacity: 0.5 }} />
        <DialogActions sx={{ px: 2, py: 1.2 }}>
          <Button onClick={() => setConsultaResult(null)} sx={{ textTransform: "none", fontFamily: "'72', Arial, sans-serif", fontSize: 13, color: "primary.main" }}>
            {t("buttons.bClose")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Rejeitar — Manifestação do Destinatário */}
      <Dialog
        open={rejeitarDialog.open}
        onClose={() => !rejeitarLoading && setRejeitarDialog({ open: false, tpEvento: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontFamily: "'72', Arial, sans-serif", fontWeight: 600, fontSize: 14, pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          {rejeitarDialog.tpEvento === "210220" ? t("buttons.rejeitarDesconhecimento") : t("buttons.rejeitarNaoRealizada")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontFamily: "'72', Arial, sans-serif", fontSize: 13, color: "text.secondary", mb: rejeitarDialog.tpEvento === "210240" ? 2 : 0 }}>
            {rejeitarDialog.tpEvento === "210220"
              ? t("nfe.evento.confirmDesconhecimento")
              : t("nfe.evento.justificativaLabel")}
          </Typography>
          {rejeitarDialog.tpEvento === "210240" && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="Justificativa"
              value={xJust}
              onChange={(e) => setXJust(e.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText={t("nfe.validation.justificationHelper", { length: xJust.length })}
              error={xJust.length > 0 && xJust.trim().length < 15}
              sx={{ "& .MuiInputBase-root": { fontFamily: "'72', Arial, sans-serif", fontSize: 13 } }}
            />
          )}
        </DialogContent>
        <Divider sx={{ opacity: 0.5 }} />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button
            onClick={() => setRejeitarDialog({ open: false, tpEvento: null })}
            disabled={rejeitarLoading}
            sx={{ textTransform: "none", fontFamily: "'72', Arial, sans-serif", fontSize: 13, color: "primary.main" }}
          >
            {t("buttons.bCancel")}
          </Button>
          <Button
            onClick={handleRejeitarConfirm}
            disabled={rejeitarLoading || (rejeitarDialog.tpEvento === "210240" && xJust.trim().length < 15)}
            disableElevation
            sx={{ textTransform: "none", fontFamily: "'72', Arial, sans-serif", fontSize: 13, fontWeight: 500, borderRadius: 0, minWidth: 90, backgroundColor: "rgba(211,47,47,0.10)", color: "#c62828", "&:hover": { backgroundColor: "rgba(211,47,47,0.18)" }, "&:disabled": { opacity: 0.5 } }}
          >
            {rejeitarLoading ? <CircularProgress size={16} /> : t("buttons.bConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notif.open}
        autoHideDuration={8000}
        onClose={() => setNotif((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notif.severity}
          onClose={() => setNotif((p) => ({ ...p, open: false }))}
          sx={{ width: "100%" }}
        >
          {notif.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}